import { useMutation } from '@apollo/client';
import { getTimeOfDay } from '@focuslee/shared';
import { ButtonDock, MainActionButton, translateTemplateText } from '@focuslee/shared-mini-app';
import { Box, Stack } from '@mui/material';
import { Text } from '@telegram-apps/telegram-ui';
import { hapticFeedback } from '@tma.js/sdk';
import { motion } from 'framer-motion';
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { addQuestCompletion } from '../client/apollo';
import { CompleteQuestMutation, CompleteQuestMutationVariables, SwitchFocusMutation, SwitchFocusMutationVariables } from '../client/generated/graphql';
import { completeQuestMutation } from '../client/mutations/completeQuest';
import { switchFocusMutation } from '../client/mutations/switchFocus';
import { CategoryIcon } from '../components/common/CategoryIcon';
import { TimePickerView } from '../components/common/TimePickerView';
import { FocusModeSkeleton } from '../components/focus/skeleton/FocusModeSkeleton';
import { Page } from '../components/Page';
import { AnalyticsEventType } from '../contexts/analyticsEvents';
import { getColor } from '../helpers/color';
import { msToTime } from '../helpers/datetime';
import { timeValueToXP } from '../helpers/experience';
import { getNextDueDate, mapTimeOfDayToGraphQLTimeOfDay } from '../helpers/quest';
import { clearTimerState } from '../helpers/timerCloudStorage';
import { useCachedCategoryWithFallback } from '../hooks/caching/category';
import { useCachedGoalCategoryWithFallback, useCachedGoalWithFallback } from '../hooks/caching/goal';
import { useCachedQuestWithFallback } from '../hooks/caching/quest';
import { useAnalytics } from '../hooks/useAnalytics';
import { useTimer } from '../hooks/useTimer';

export const FocusModePage: React.FC = () => {
  const { questId } = useParams<{ questId: string }>();
  const navigate = useNavigate();
  const { recordEvent } = useAnalytics();
  const { t } = useTranslation('common');
  const [switchFocus, { loading: switchLoading }] = useMutation<SwitchFocusMutation, SwitchFocusMutationVariables>(switchFocusMutation);
  const [completeQuest, { loading: completeLoading }] = useMutation<CompleteQuestMutation, CompleteQuestMutationVariables>(completeQuestMutation, { fetchPolicy: 'no-cache' });
  const { quest, loading: questLoading } = useCachedQuestWithFallback(questId);
  
  const { category: cachedCategory, loading: categoryLoading } = useCachedCategoryWithFallback(quest?.categoryId);
  const { goal: cachedGoal, loading: goalLoading } = useCachedGoalWithFallback(quest?.goalId);

  const category = useMemo(() => quest?.category ?? cachedCategory, [quest?.categoryId]);
  const goal = useMemo(() => quest?.goal ?? cachedGoal, [quest?.goal, cachedGoal]);
  const { category: goalCategory, loading: goalCategoryLoading } = useCachedGoalCategoryWithFallback(goal);
  
  const { elapsedTime, isRunning, start, pause, setTime } = useTimer(quest?.timerValueInMs || 0, questId);
  const isLoading = switchLoading || completeLoading || questLoading || categoryLoading || goalLoading || goalCategoryLoading;
  const [showTimePicker, setShowTimePicker] = useState(false);
  const completionXP = timeValueToXP(elapsedTime);
  const questName = quest ? translateTemplateText(t, quest.templateId, 'name', quest.name) : '';
  const questDescription = quest ? translateTemplateText(t, quest.templateId, 'description', quest.description ?? '') : '';
  const categoryName = category ? translateTemplateText(t, category.templateId, 'name', category.name) : '';

  const getTimeFontSize = () => {
    const isLongTime = elapsedTime >= 3600000;
    const isLongInterval = quest?.timedInterval && quest.timedInterval >= 60;
    const noInterval = !quest?.timedInterval;
    
    if (noInterval || (!isLongTime && !isLongInterval))
        return '48px';
    
    return isLongInterval && isLongTime ? '30px' : '36px';
  };
  const timeFontSize = useMemo(() => getTimeFontSize(), [elapsedTime, quest?.timedInterval]);

  const formatTime = (elapsed: number, target?: number | null) => {
    if (target) {
      return `${msToTime(elapsed)} / ${msToTime(target * 60 * 1000)}`;
    }
    return msToTime(elapsed);
  };

  const handleStartPause = useCallback(() => {
    hapticFeedback.impactOccurred.ifAvailable('medium');
    if (isRunning) {
      pause();
    } else {
      start();
    }
  }, [isRunning, pause, start]);
  
  const handleClose = useCallback(() => {
    navigate(-1);
  }, []);

  const handleSwitchFocus = useCallback(async () => {
    if (!questId) return;
    hapticFeedback.impactOccurred.ifAvailable('medium');
    try {
      await switchFocus({ variables: { questId, timerValueInMs: elapsedTime } });
      await clearTimerState();
      recordEvent(AnalyticsEventType.SWITCH_FOCUS, { questId, elapsedTimeMs: elapsedTime });
      handleClose();
    } catch (error) {
      console.error('Error switching focus:', error);
    }
  }, [questId, elapsedTime, handleClose, recordEvent]);

  const handleComplete = useCallback(async () => {
    if (!questId || !quest) return;
    hapticFeedback.impactOccurred.ifAvailable('medium');
    try {
      const newDueDate = getNextDueDate(quest);
      const timeOfDay = getTimeOfDay(quest.dueDate);
      const completionResult = await completeQuest({
        variables: {
          questId: questId,
          dueDate: newDueDate,
          timerValueInMs: elapsedTime,
          timeOfDay: mapTimeOfDayToGraphQLTimeOfDay(timeOfDay)
        }
      });
      await clearTimerState();
      
      const completionData = completionResult.data?.completeQuest;
      if (completionData) {
        addQuestCompletion(completionData);
      }
      
      handleClose();
    } catch (error) {
      console.error('Error completing quest:', error);
    }
  }, [questId, elapsedTime, handleClose, quest, recordEvent]);

  const handleTimeDisplayClick = () => {
    if (!isRunning) {
      setShowTimePicker(true);
    }
  };

  const handleTimeUpdate = useCallback((newTime: number) => {
    pause();
    setTime(newTime);
    setShowTimePicker(false);
  }, [pause, setTime]);

  const handleTimeCancel = useCallback(() => {
    setShowTimePicker(false);
  }, []);

  if (showTimePicker) {
    return (
      <TimePickerView
        initialTimeMs={elapsedTime}
        onUpdate={handleTimeUpdate}
        onCancel={handleTimeCancel}
      />
    );
  }

  if (!questId) {
    return null;
  }

  if (questLoading || !quest) {
    return <FocusModeSkeleton />;
  }

  return (
    <Page
      back={true}
      insetEnabled={false}
      backClick={handleSwitchFocus}
      footer={
        <ButtonDock
          secondary={{ text: t('quest.focus.switch'), onClick: handleSwitchFocus, enabled: !isLoading }}
          main={{ text: isRunning ? t('quest.focus.pause') : t('quest.focus.start'), onClick: handleStartPause, enabled: !isLoading, loading: isLoading }}
        />
      }
    >
      <Stack alignItems="center" justifyContent="center" minHeight="var(--tg-viewport-content-safe-area-inset-top)">
        <Text weight="2" style={{ fontSize: '18px' }}>
          {t('quest.focus.title')}
        </Text>
      </Stack>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
        style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', padding: '10px 20px', gap: 12, alignItems: 'center', justifyContent: 'center' }}
        key="focus-content"
      >
        <Text weight="2" style={{ fontSize: '26px', textAlign: 'center' }}>{questName}</Text>
        {questDescription && (
          <Text style={{ fontSize: '16px', color: 'var(--tg-theme-hint-color)', textAlign: 'center', maxWidth: '300px' }}>
            {questDescription}
          </Text>
        )}
        <Stack alignItems="center" onClick={handleTimeDisplayClick} style={{ cursor: isRunning ? 'default' : 'pointer' }}>
          <Text weight="3" style={{ fontSize: timeFontSize, fontFamily: 'monospace', lineHeight: '1.2', whiteSpace: 'nowrap', color: 'var(--tg-theme-button-color)' }}>
            {formatTime(elapsedTime, quest.timedInterval)}
          </Text>
        </Stack>
        <Stack gap={1} alignItems="center">
          <Stack direction="row" alignItems="center" spacing={1}>
            <Text weight="2" style={{ color: goal && goal.active && goalCategory ? getColor(goalCategory.iconColor) : 'var(--tg-theme-hint-color)', fontSize: '26px' }}>
              +{completionXP} XP
            </Text>
            {goalCategory && <CategoryIcon icon={goalCategory.icon} iconColor={goalCategory.iconColor} customIcon={goalCategory.customIcon} color={getColor(goalCategory.iconColor)} size={30} type="filled" />}
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            {category && (
              <Box sx={{
                width: 20,
                height: 20,
                borderRadius: 1,
                background: getColor(category.iconColor),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CategoryIcon 
                  icon={category.icon} 
                  iconColor={category.iconColor} 
                  customIcon={category.customIcon} 
                  size={14} 
                  type="filled" 
                  color="var(--tg-theme-text-color)" 
                />
              </Box>
            )}
            {category && <Text style={{ color: 'var(--tg-theme-hint-color)' }}>{categoryName}</Text>}
          </Stack>
        </Stack>
        <Box sx={{ mb: 2 }}>
          <MainActionButton
            text={t('quest.complete')}
            onClick={handleComplete}
            loading={isLoading}
            enabled={!(isLoading || isRunning || elapsedTime === 0 || Boolean(quest.timedInterval && elapsedTime < quest.timedInterval * 60 * 1000))}
          />
        </Box>
      </motion.div>
    </Page>
  );
};
